import { useEffect, useState, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Trophy, Zap, AlertTriangle, ArrowRight, Play, Bell, Award, Users,
  Loader2, CheckCircle2, RotateCcw, Plus, Minus, Crosshair, Server,
  X as XIcon, Filter,
} from 'lucide-react'
import { io } from 'socket.io-client'
import { tournamentsAPI, adminAPI, mapvetoAPI, gameServersAPI } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import AccentCard from '@/components/AccentCard'
import PageHeader from '@/components/PageHeader'

/* Toernooi-regie — dashboard voor admin/referee tijdens de LAN.
   Toont actieve toernooien in een grid, met per kaart de lopende match,
   ready-check, snelle winner/score-invoer en een "roep spelers"-knop.

   Realtime via socket op tournament:updated en tournament:match:updated
   met een 15s poll als backup. Bewust GEEN check-in-warnings (feature
   wordt niet gebruikt op deze LAN). */

const POLL_MS = 15_000

function relatiefMin(iso) {
  if (!iso) return null
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 1) return 'net'
  return `${min}m`
}

/* Live-klok voor actieve matches: elke 5s, seconden-precisie in mm:ss. */
function useTicker(ms = 5000) {
  const [, setTick] = useState(0)
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), ms)
    return () => clearInterval(iv)
  }, [ms])
}
function matchDuur(iso) {
  if (!iso) return ''
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

/* Waarschuwingsgeluid — één keer per nieuwe stale-match. AudioContext lazy
   zodat browsers zonder autoplay-permission niet crashen. */
function useWarningBeep(hasNieuwe) {
  const ctxRef = useRef(null)
  useEffect(() => {
    if (!hasNieuwe) return
    try {
      if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)()
      const ctx = ctxRef.current
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.type = 'sine'; o.frequency.value = 880
      g.gain.value = 0.08
      o.connect(g); g.connect(ctx.destination)
      o.start(); o.stop(ctx.currentTime + 0.18)
    } catch {}
  }, [hasNieuwe])
}

export default function TournamentRegiePage() {
  const [lijst, setLijst]   = useState([])
  const [hub, setHub]       = useState(null)
  const [loading, setLoad]  = useState(true)
  const [busy, setBusy]     = useState(null)
  const [nieuweStale, setNieuweStale] = useState(false)
  // Map van match_id → mapveto-sessie (laatste), voor de "Map-veto" knop
  // in MatchInline. Wordt bij elke poll ververst zodat een net-gestarte veto
  // meteen als link zichtbaar is.
  const [vetoByMatch, setVetoByMatch] = useState({})
  // Lijst van game-servers + welke match ze nu hosten — voor de "op server"
  // dropdown per actieve match (overlay-context F1).
  const [serverList, setServerList] = useState([])
  // Filter-state: als je één toernooi wil focussen. null = alles tonen.
  // Bewaard in localStorage zodat een refresh je selectie niet vergeet.
  const [filterId, setFilterIdState] = useState(() => {
    const v = localStorage.getItem('regie-tournament-filter')
    return v ? Number(v) : null
  })
  const setFilterId = (v) => {
    setFilterIdState(v)
    if (v) localStorage.setItem('regie-tournament-filter', String(v))
    else localStorage.removeItem('regie-tournament-filter')
  }
  const bekendeStale = useRef(new Set())
  useWarningBeep(nieuweStale)
  // Live-klok tikt globaal zodat match-duur automatisch bijwerkt zonder re-fetch.
  useTicker(5000)

  const laadHub = useCallback(() => {
    adminAPI.getHub().then(r => {
      const data = r.data
      setHub(data)
      // Detecteer nieuwe stale-matches → geluid + kort-lopende "nieuwe"-flag
      // die de useWarningBeep hook triggert. Bekendeset onthoudt ze zodat
      // dezelfde match niet elke poll opnieuw piept.
      const stales = (data?.live?.stale_matches || []).map(m => m.id)
      const nieuwe = stales.filter(id => !bekendeStale.current.has(id))
      if (nieuwe.length) {
        nieuwe.forEach(id => bekendeStale.current.add(id))
        setNieuweStale(true)
        setTimeout(() => setNieuweStale(false), 500)
      }
    }).catch(() => {})
  }, [])

  const laadVetos = useCallback(() => {
    mapvetoAPI.recent().then(r => {
      const map = {}
      for (const s of (r.data?.sessions || [])) {
        if (s.match_id && !map[s.match_id]) map[s.match_id] = s
      }
      setVetoByMatch(map)
    }).catch(() => {})
  }, [])

  const laadServers = useCallback(() => {
    gameServersAPI.getAllServers().then(r => {
      setServerList((r.data?.servers || r.data || []).filter(s => s.overlay_identifier))
    }).catch(() => {})
  }, [])

  const laadToernooien = useCallback(async () => {
    try {
      const r = await tournamentsAPI.getTournaments()
      const alle = r.data?.tournaments || []
      // Alleen actieve statussen (registration/ready/active/in_progress).
      // Plus recent-completed (laatste 4u) voor "Net afgelopen" sectie.
      const relevant = alle.filter(t =>
        ['registration', 'ready', 'active', 'in_progress'].includes(t.status)
        || (t.status === 'completed' && (
          !t.points_awarded_at ||
          (t.badge_type_id && !t.badges_awarded_at) ||
          (t.prize_id && !t.prize_awarded_at)
        ))
      )
      // Details ophalen voor elk (matches + participants + ready)
      const detail = await Promise.all(relevant.map(t =>
        tournamentsAPI.getTournament(t.id)
          .then(rr => rr.data)
          .catch(() => null)))
      setLijst(detail.filter(Boolean))
    } finally { setLoad(false) }
  }, [])

  useEffect(() => {
    laadHub(); laadToernooien(); laadVetos(); laadServers()
    const iv = setInterval(() => { laadHub(); laadToernooien(); laadVetos(); laadServers() }, POLL_MS)

    const s = io({
      auth: { token: localStorage.getItem('auth_token') },
      transports: ['polling', 'websocket'],
      reconnection: true,
    })
    s.on('tournament:updated', laadToernooien)
    s.on('tournament:match:updated', laadToernooien)
    return () => { clearInterval(iv); s.disconnect() }
  }, [laadHub, laadToernooien, laadVetos, laadServers])

  const activiefAll = lijst.filter(d => ['registration','ready','active','in_progress'].includes(d.tournament?.status))
  const netAfAll    = lijst.filter(d => d.tournament?.status === 'completed')
  // Als het filter naar een verdwenen tournament wijst: stil resetten.
  const filterKlopt = filterId != null && [...activiefAll, ...netAfAll].some(d => d.tournament?.id === filterId)
  useEffect(() => { if (filterId != null && !loading && !filterKlopt) setFilterId(null) },
            [filterId, filterKlopt, loading])
  const activief   = filterKlopt ? activiefAll.filter(d => d.tournament?.id === filterId) : activiefAll
  const netAf      = filterKlopt ? netAfAll.filter(d => d.tournament?.id === filterId)    : netAfAll
  const activeFilterName = filterKlopt
    ? [...activiefAll, ...netAfAll].find(d => d.tournament?.id === filterId)?.tournament?.name
    : null

  const staleMatches = hub?.live?.stale_matches || []

  if (loading) return (
    <div className="p-6 max-w-6xl mx-auto"><Loader2 className="animate-spin" /></div>
  )

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <PageHeader
        icon={Trophy}
        title="Toernooi-regie"
        subtitle="Alle actieve toernooien in één blik — voor de admin/referee tijdens de LAN"
      />

      {/* Filter-strip: ≤ 5 toernooien = knoppen, meer = dropdown.
          Als er een filter actief is, verschijnt bovenaan een prominente
          banner met de naam + wis-knop zodat je nooit vergeet dat de
          weergave beperkt is. */}
      {(activiefAll.length + netAfAll.length) > 1 && (
        <FilterStrip
          alle={[...activiefAll, ...netAfAll]}
          filterId={filterId}
          setFilterId={setFilterId}
        />
      )}
      {filterKlopt && (
        <div className="rounded-md border border-acid/60 bg-acid/10 px-3 py-2 flex items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-acid shrink-0" />
          <span className="flex-1">
            Filter actief: <b>{activeFilterName}</b>. Andere toernooien zijn verborgen.
          </span>
          <Button size="sm" variant="outline" onClick={() => setFilterId(null)}>
            <XIcon className="h-3 w-3 mr-1" /> Wis filter
          </Button>
        </div>
      )}

      {staleMatches.length > 0 && (
        <div className="space-y-2">
          {staleMatches.map(m => (
            <Link key={m.id} to={`/tournaments/${m.tournament_id}`} className="block no-underline">
              <AccentCard tone="peach" className="hover:border-peach">
                <CardContent className="p-3 flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-peach shrink-0" />
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="font-semibold">Match wacht al {m.minuten}m op resultaat</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {m.tournament_name} — ronde {m.round} match {m.match_number}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </CardContent>
              </AccentCard>
            </Link>
          ))}
        </div>
      )}

      {activief.length === 0 && netAf.length === 0 && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">
          Geen actieve toernooien. Start er één via <Link to="/tournaments" className="text-acid underline">/tournaments</Link>.
        </CardContent></Card>
      )}

      {activief.length > 0 && (
        <>
          <div className="eyebrow">Actieve toernooien ({activief.length})</div>
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {activief.map(d => (
              <ToernooiKaart key={d.tournament.id} data={d} busy={busy} setBusy={setBusy}
                             onRefresh={() => { laadToernooien(); laadVetos(); laadServers() }}
                             vetoByMatch={vetoByMatch}
                             serverList={serverList} />
            ))}
          </div>
        </>
      )}

      {netAf.length > 0 && (
        <>
          <div className="eyebrow pt-4">Net afgelopen — klaar voor uitreiking ({netAf.length})</div>
          <div className="grid gap-2">
            {netAf.map(d => (
              <Link key={d.tournament.id} to={`/tournaments/${d.tournament.id}?tab=uitreiking`}
                    className="no-underline">
                <AccentCard tone="gold" className="hover:border-gold">
                  <CardContent className="p-3 flex items-center gap-3">
                    <Award className="h-4 w-4 text-gold shrink-0" />
                    <div className="flex-1 text-sm">
                      <div className="font-semibold">{d.tournament.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {d.tournament.game} · afgesloten — nog uit te reiken
                      </div>
                    </div>
                    <span className="text-xs text-gold font-semibold uppercase tracking-wider">Uitreiken →</span>
                  </CardContent>
                </AccentCard>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function ToernooiKaart({ data, busy, setBusy, onRefresh, vetoByMatch = {}, serverList = [] }) {
  const t = data.tournament
  const matches = data.matches || []
  const parts = data.participants || []
  // CS 1.6 (of vergelijkbaar) → mapveto-blok in MatchInline aanzetten.
  const isCS16 = /1\.?6|cs16|counter[- ]?strike\s*1/i.test(String(t.game || ''))
  // Backend zet ready_participant_ids per match; wij bouwen de map lokaal
  // voor snelle lookups in de rijen.
  const readyMap = Object.fromEntries(matches.map(m => [m.id, m.ready_participant_ids || []]))

  // FFA-heats hebben geen 1v1 participants; die active-match zoeken we
  // apart zodat de kaart óók iets toont voor FFA-toernooien tijdens de LAN.
  const isFFA = t.type === 'ffa'
  const isSwiss = t.type === 'swiss'
  const isGroupKO = t.type === 'group_ko'
  const isLadder = t.type === 'ladder'
  const active = isFFA
    ? (matches.find(m => m.status === 'in_progress' && m.bracket_type === 'ffa')
        || matches.find(m => m.status === 'pending' && m.bracket_type === 'ffa'))
    : (matches.find(m => m.status === 'in_progress')
        || matches.find(m => m.status === 'pending' && m.participant1_id && m.participant2_id))
  const nextRound = matches.filter(m => m.status === 'pending' && m.participant1_id && m.participant2_id && m.id !== active?.id).slice(0, 3)

  // Swiss: kunnen we volgende ronde pairen?
  const swissRounds = Number(t.features?.swiss_rounds || 0)
  const currentRound = matches.length ? Math.max(...matches.map(m => m.round)) : 0
  const currentRoundMatches = matches.filter(m => m.round === currentRound)
  const currentRoundDone = currentRoundMatches.length > 0 && currentRoundMatches.every(m => m.status === 'completed')
  const canPairSwiss = isSwiss && currentRoundDone && swissRounds > 0 && currentRound < swissRounds

  // Group+KO: kunnen we knockout starten?
  const groupMatches = matches.filter(m => m.phase === 'group')
  const koMatches = matches.filter(m => m.phase === 'knockout')
  const canStartKO = isGroupKO && groupMatches.length > 0
    && groupMatches.every(m => m.status === 'completed')
    && koMatches.length === 0

  const rondeInfo = (() => {
    const rondes = [...new Set(matches.map(m => m.round))].sort((a, b) => a - b)
    if (!rondes.length) return null
    const done = rondes.filter(r => matches.filter(m => m.round === r).every(m => m.status === 'completed')).length
    return { done, total: rondes.length, huidig: active?.round }
  })()

  const isDeathmatch = t.type === 'deathmatch'
  const tone = active ? 'live' : t.status === 'registration' ? 'peach' : 'acid'

  return (
    <AccentCard tone={tone}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="eyebrow">{t.game}{rondeInfo?.huidig ? ` · ronde ${rondeInfo.huidig}/${rondeInfo.total}` : ''}</div>
            <div className="font-semibold text-base truncate">
              <Link to={`/tournaments/${t.id}`} className="hover:text-acid">{t.name}</Link>
            </div>
          </div>
          <Badge variant={active ? 'default' : 'outline'} className="text-xs shrink-0">{t.status}</Badge>
        </div>

        {/* Regie-hoofdactie voor formats waar één klik het toernooi verder brengt.
            Voordien zat dit 2-3 klikken diep op de detail-pagina. */}
        {(canPairSwiss || canStartKO) && (
          <div className="rounded-md border border-acid/40 bg-acid/5 p-2 flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {canPairSwiss
                ? `Ronde ${currentRound}/${swissRounds} af — klaar voor volgende ronde.`
                : `Group-stage af — klaar voor knockout.`}
            </span>
            <RegieActieKnop tournamentId={t.id}
                            kind={canPairSwiss ? 'swiss' : 'groupko'}
                            busy={busy} setBusy={setBusy} onRefresh={onRefresh} />
          </div>
        )}

        {active && !isFFA && (
          <div className="rounded-md border border-live/30 bg-live/5 p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <span className="live-dot">Bezig</span>
              <span className="text-muted-foreground">
                Match {active.round}.{active.match_number}
              </span>
              {active.status === 'in_progress' && (
                <span className="font-mono text-live tabular-nums" title="Duur sinds start">
                  ⧗ {matchDuur(active.updated_at)}
                </span>
              )}
            </div>
            <MatchInline
              tournamentId={t.id}
              match={active}
              parts={parts}
              readyIds={readyMap[active.id] || []}
              isDeathmatch={isDeathmatch}
              isLadder={isLadder}
              isCS16={isCS16}
              veto={vetoByMatch[active.id] || null}
              serverList={serverList}
              busy={busy}
              setBusy={setBusy}
              onRefresh={onRefresh}
            />
            <NotesInline tournamentId={t.id} match={active} busy={busy} setBusy={setBusy} onRefresh={onRefresh} />
          </div>
        )}

        {/* FFA-heats hebben geen 1v1; wij tonen de deelnemers-lijst + link
            naar de detail-pagina waar admin de scores invoert. */}
        {active && isFFA && (
          <FFAHeatKaart tournamentId={t.id} match={active} onRefresh={onRefresh} />
        )}

        {matches.length > 0 && (
          <BracketMini matches={matches} activeRound={active?.round} />
        )}

        {nextRound.length > 0 && (
          <div className="space-y-1.5">
            <div className="eyebrow">Wachtend</div>
            {nextRound.map(m => (
              <MatchRij key={m.id} match={m} tournamentId={t.id}
                        readyIds={readyMap[m.id] || []} busy={busy} setBusy={setBusy}
                        onRefresh={onRefresh} />
            ))}
          </div>
        )}

        {!active && matches.length === 0 && t.status === 'registration' && (
          <div className="text-xs text-muted-foreground">
            Nog geen bracket. Deelnemers: {parts.length}.
          </div>
        )}

        <div className="flex items-center justify-between pt-1 text-xs">
          <span className="text-muted-foreground">
            <Users className="h-3 w-3 inline mr-1" />
            {parts.length} deelnemer{parts.length === 1 ? '' : 's'}
            {(() => {
              const noShows = parts.filter(p => p.no_show).length
              return noShows > 0 && (
                <span className="ml-2 text-slate-400" title="No-show — uitgesloten bij bracket-start">
                  · {noShows} no-show
                </span>
              )
            })()}
          </span>
          <Link to={`/tournaments/${t.id}`}
                className="text-acid font-semibold uppercase tracking-wider">
            Detail →
          </Link>
        </div>
      </CardContent>
    </AccentCard>
  )
}

/* Compacte rij voor een wachtende match — ready-status + roep-spelers-knop. */
function MatchRij({ match, tournamentId, readyIds, busy, setBusy, onRefresh }) {
  const roepen = async () => {
    setBusy(`call-${match.id}`)
    try { await tournamentsAPI.callMatchPlayers(tournamentId, match.id); onRefresh() }
    catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(null) }
  }
  const b = busy === `call-${match.id}`
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="font-mono text-muted-foreground w-10 shrink-0">{match.round}.{match.match_number}</span>
      <span className="flex-1 truncate">
        {match.participant1_name || '?'} <span className="text-muted-foreground">vs</span> {match.participant2_name || '?'}
      </span>
      <ReadyDots readyIds={readyIds}
                 participants={[match.participant1_id, match.participant2_id].filter(Boolean)} />
      <Button size="sm" variant="ghost" title="Roep de spelers via notificatie"
              disabled={b} onClick={roepen} className="h-6 px-1.5">
        <Bell className="h-3 w-3" />
      </Button>
    </div>
  )
}

function ReadyDots({ readyIds, participants }) {
  return (
    <span className="flex gap-0.5 shrink-0">
      {participants.map(pid => (
        <span key={pid} className={`h-2 w-2 rounded-full ${readyIds.includes(pid) ? 'bg-live' : 'bg-muted'}`}
              title={readyIds.includes(pid) ? 'Ready' : 'Wacht op ready'} />
      ))}
    </span>
  )
}

/* Inline match-controls: winner-select + score-input + roep spelers.
   Voor deathmatch: per-participant getal. Voor normal (bv CS): +knoppen
   die "16-14"-string automatisch bijhouden in de score-TEXT. */
function MatchInline({ tournamentId, match, parts, readyIds, isDeathmatch, isLadder, isCS16, veto, serverList, busy, setBusy, onRefresh }) {
  const p1 = parts.find(p => p.id === match.participant1_id)
  const p2 = parts.find(p => p.id === match.participant2_id)

  const [dm, setDm] = useState({}) // { participant_id: score }
  useEffect(() => { setDm({}) }, [match.id])

  const scoreParts = (match.score || '0-0').split('-').map(x => parseInt(x || '0', 10) || 0)
  const [s1, setS1] = useState(scoreParts[0] || 0)
  const [s2, setS2] = useState(scoreParts[1] || 0)
  useEffect(() => {
    const [a, b] = (match.score || '0-0').split('-').map(x => parseInt(x || '0', 10) || 0)
    setS1(a); setS2(b)
  }, [match.id, match.score])

  const doeCall = async (kind, args) => {
    const key = `${kind}-${match.id}`
    setBusy(key)
    try {
      if (kind === 'winner') {
        await tournamentsAPI.updateMatch(tournamentId, match.id, { winner_id: args, status: 'completed', score: match.score || `${s1}-${s2}` })
      } else if (kind === 'score') {
        await tournamentsAPI.updateMatch(tournamentId, match.id, { score: `${s1}-${s2}`, status: match.status === 'pending' ? 'in_progress' : match.status })
      } else if (kind === 'dm') {
        const list = Object.entries(dm).map(([pid, sc]) => ({ participant_id: Number(pid), score: Number(sc) }))
        await tournamentsAPI.updateDeathmatchScores(tournamentId, match.id, list)
      } else if (kind === 'call') {
        await tournamentsAPI.callMatchPlayers(tournamentId, match.id)
      } else if (kind === 'resetReady') {
        await tournamentsAPI.resetMatchReady(tournamentId, match.id)
      } else if (kind === 'noshow') {
        // args = { loser_participant_id, winner_participant_id }
        // adminMarkNoShow doet nu ook de cascade (openstaande matches waar
        // deze speler in zit → tegenstander wint via NO-SHOW + doorschuif).
        // Deze match zit daar dus al bij — geen aparte updateMatch nodig.
        if (!window.confirm('Zeker? Deze speler wordt als no-show gemarkeerd, valt UIT het toernooi en de tegenstander wint deze match automatisch.')) {
          setBusy(null); return
        }
        await tournamentsAPI.adminMarkNoShow(tournamentId, args.loser_participant_id, true)
      } else if (kind === 'double-noshow') {
        // Belangrijk: eerst de match zelf als NO-CONTEST markeren (winner=null),
        // pas dan de deelnemers uitschakelen — anders zou de cascade van de
        // eerste no-show de tegenstander (nog niet no-show) doorschuiven.
        if (!window.confirm('Beide teams komen niet opdagen? Match wordt gemarkeerd als NO-CONTEST, beide spelers uit het toernooi, GEEN winnaar naar de volgende ronde. Bracket-slot in de volgende ronde blijft leeg — pas eventueel handmatig aan.')) {
          setBusy(null); return
        }
        await tournamentsAPI.updateMatch(tournamentId, match.id, {
          winner_id: null,
          status: 'completed',
          score: 'NO-CONTEST',
        })
        if (match.participant1_id) await tournamentsAPI.adminMarkNoShow(tournamentId, match.participant1_id, true)
        if (match.participant2_id) await tournamentsAPI.adminMarkNoShow(tournamentId, match.participant2_id, true)
      }
      onRefresh()
    } catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(null) }
  }

  if (isDeathmatch) {
    const teilnemer = [p1, p2].filter(Boolean)
    return (
      <div className="space-y-2">
        {teilnemer.map(p => (
          <div key={p.id} className="flex items-center gap-2 text-sm">
            <span className="flex-1 truncate">{p.team_name || p.username}</span>
            <Input type="number" min="0" className="w-20 h-8 text-right tabular-nums"
                   defaultValue={0} onChange={e => setDm(prev => ({ ...prev, [p.id]: e.target.value }))} />
          </div>
        ))}
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={() => doeCall('dm')} disabled={busy === `dm-${match.id}`}>
            Scores opslaan
          </Button>
          <Button size="sm" variant="outline" onClick={() => doeCall('call')} disabled={busy === `call-${match.id}`}>
            <Bell className="h-3 w-3 mr-1" /> Roep spelers
          </Button>
        </div>
      </div>
    )
  }

  const isBO = Number(match.best_of || 1) > 1

  return (
    <div className="space-y-2">
      {isBO && (
        <div className="text-[11px] font-mono uppercase tracking-widest text-acid/90 bg-acid/5 border border-acid/30 rounded px-2 py-1">
          BO{match.best_of} — per-game scores voer je in via het bracket-overzicht (Match klik → dialoog).
          Hier kan je alleen de winnaar direct kiezen.
        </div>
      )}
      {isCS16 && (
        <MapvetoBlok tournamentId={tournamentId} match={match} veto={veto}
                     busy={busy} setBusy={setBusy} onRefresh={onRefresh} />
      )}
      <ServerKoppelBlok tournamentId={tournamentId} match={match}
                        serverList={serverList} busy={busy} setBusy={setBusy}
                        onRefresh={onRefresh} />
      <div className="grid grid-cols-2 gap-2">
        {/* Voor ladder: no-show is semantisch niet "uit toernooi", maar
            "challenger neemt over". Verberg de no-show-knop dus per zijde. */}
        <ScoreZij naam={p1?.team_name || p1?.username || '?'} val={s1}
                  setVal={setS1} accent="#B4FF39"
                  isReady={readyIds.includes(match.participant1_id)}
                  onWin={() => doeCall('winner', match.participant1_id)}
                  onNoShow={isLadder ? null : () => doeCall('noshow', { loser_participant_id: match.participant1_id, winner_participant_id: match.participant2_id })}
                  busy={busy?.startsWith('winner-') || busy?.startsWith('noshow-') ? busy.endsWith(String(match.id)) : false}
                  hideScore={isBO} />
        <ScoreZij naam={p2?.team_name || p2?.username || '?'} val={s2}
                  setVal={setS2} accent="#7BC8FF"
                  isReady={readyIds.includes(match.participant2_id)}
                  onWin={() => doeCall('winner', match.participant2_id)}
                  onNoShow={isLadder ? null : () => doeCall('noshow', { loser_participant_id: match.participant2_id, winner_participant_id: match.participant1_id })}
                  busy={busy?.startsWith('winner-') || busy?.startsWith('noshow-') ? busy.endsWith(String(match.id)) : false}
                  hideScore={isBO} />
      </div>
      <div className="flex gap-2 pt-1 flex-wrap">
        {!isBO && (
          <Button size="sm" variant="outline" onClick={() => doeCall('score')} disabled={busy === `score-${match.id}`}>
            Score opslaan
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => doeCall('call')} disabled={busy === `call-${match.id}`}>
          <Bell className="h-3 w-3 mr-1" /> Roep spelers
        </Button>
        {readyIds.length > 0 && (
          <Button size="sm" variant="ghost" title="Ready resetten"
                  onClick={() => doeCall('resetReady')} disabled={busy === `resetReady-${match.id}`}>
            <RotateCcw className="h-3 w-3" />
          </Button>
        )}
        {!isLadder && (
          <Button size="sm" variant="ghost" className="text-[10px] text-muted-foreground hover:text-signal ml-auto"
                  onClick={() => doeCall('double-noshow')} disabled={busy === `double-noshow-${match.id}`}
                  title="Beide teams zijn niet komen opdagen — match cancellen, beiden uit toernooi">
            Beide no-show
          </Button>
        )}
      </div>
    </div>
  )
}

/* Compacte notities per match — één textarea, save-on-blur. */
function NotesInline({ tournamentId, match, busy, setBusy, onRefresh }) {
  const [notes, setNotes] = useState(match.notes || '')
  const [dirty, setDirty] = useState(false)
  useEffect(() => { setNotes(match.notes || ''); setDirty(false) }, [match.id, match.notes])

  const save = async () => {
    if (!dirty) return
    setBusy(`notes-${match.id}`)
    try {
      await tournamentsAPI.updateMatch(tournamentId, match.id, { notes })
      setDirty(false)
      onRefresh()
    } catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(null) }
  }

  return (
    <div className="pt-1 border-t border-border/40">
      <textarea
        rows={1}
        value={notes}
        placeholder="Ref-notities (protest, timeout, incident)…"
        className="w-full text-xs bg-transparent border-0 outline-none resize-none text-muted-foreground focus:text-foreground"
        onChange={e => { setNotes(e.target.value); setDirty(true) }}
        onBlur={save}
      />
      {dirty && (
        <div className="text-[10px] text-peach">niet opgeslagen — tab uit veld om te bewaren</div>
      )}
    </div>
  )
}

/* Mini-bracket: één horizontale strip per ronde met status-dots. Verticaal
   compact, ~30-60px hoog. Alleen om te zien "waar zitten we in de bracket". */
function BracketMini({ matches, activeRound }) {
  const rondes = [...new Set(matches.map(m => m.round))].sort((a, b) => a - b)
  return (
    <div className="pt-2 border-t border-border/40">
      <div className="eyebrow mb-1.5">Bracket</div>
      <div className="flex gap-2 overflow-x-auto">
        {rondes.map(r => {
          const rMatches = matches.filter(m => m.round === r)
          const isActive = r === activeRound
          return (
            <div key={r} className="flex flex-col items-center gap-1 shrink-0"
                 style={{ opacity: isActive ? 1 : 0.7 }}>
              <div className="text-[9px] font-mono uppercase text-muted-foreground">R{r}</div>
              <div className="flex flex-col gap-0.5">
                {rMatches.map(m => (
                  <span key={m.id}
                        className="h-1.5 w-6 rounded-full"
                        title={`Match ${m.round}.${m.match_number} · ${m.status}`}
                        style={{
                          background: m.status === 'completed' ? '#61E294'
                                    : m.status === 'in_progress' ? 'rgb(var(--live-rgb))'
                                    : '#94A3B8',
                        }} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ScoreZij({ naam, val, setVal, accent, isReady, onWin, onNoShow, busy, hideScore }) {
  return (
    <div className="rounded-md border border-border p-2 space-y-1"
         style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${isReady ? 'bg-live' : 'bg-muted'}`}
              title={isReady ? 'Ready' : 'Wacht op ready'} />
        <span className="text-xs font-semibold truncate">{naam}</span>
      </div>
      {!hideScore && (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                  onClick={() => setVal(Math.max(0, val - 1))}>
            <Minus className="h-3 w-3" />
          </Button>
          <span className="flex-1 text-center text-xl font-bold tabular-nums" style={{ color: accent }}>{val}</span>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                  onClick={() => setVal(val + 1)}>
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      )}
      {/* Winnaar-vaststelling is onomkeerbaar en bepaalt de rest van de
          bracket → hold-to-fire i.p.v. één-klik zodat een per-ongeluk-tap
          niet meteen de match afsluit. */}
      <HoldWinnerButton onFire={onWin} disabled={busy} accent={accent} />
      {onNoShow && (
        <Button size="sm" variant="ghost" className="w-full h-6 text-[10px] text-muted-foreground hover:text-signal"
                disabled={busy} onClick={onNoShow}
                title="Deze speler kwam niet opdagen — tegenstander wint automatisch en deze speler valt uit het toernooi">
          no-show
        </Button>
      )}
    </div>
  )
}


/* Regie-hoofdactie knop voor Swiss "next round" en Group+KO "start knockout".
   Deze acties zaten voordien alleen op de detail-pagina — te veel klikken
   diep tijdens LAN. */
function RegieActieKnop({ tournamentId, kind, busy, setBusy, onRefresh }) {
  const label = kind === 'swiss' ? 'Volgende ronde' : 'Start knockout'
  const key = `regie-${kind}-${tournamentId}`
  const run = async () => {
    setBusy(key)
    try {
      if (kind === 'swiss') await tournamentsAPI.generateNextSwissRound(tournamentId)
      else                  await tournamentsAPI.startGroupKnockout(tournamentId)
      onRefresh()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusy(null) }
  }
  return (
    <Button size="sm" disabled={busy === key} onClick={run}>
      <Play className="h-3 w-3 mr-1" />
      {busy === key ? 'Bezig…' : label}
    </Button>
  )
}

/* Map-veto blok voor CS 1.6 matches — één regel in de active-match kaart.
   - Nog geen sessie: knop "Start map-veto" (creëert sessie gekoppeld aan
     tournament_id + match_id, opent /mapveto/:uuid).
   - Wel sessie: link naar de vetopagina + huidige status (map of "beurt aan
     ...").
   Vereist dat beide participants aan een team gekoppeld zijn (team_id op de
   tournament_participants rij). */
function MapvetoBlok({ tournamentId, match, veto, busy, setBusy, onRefresh }) {
  const navigate = useNavigate()
  const t1 = match.participant1_team_id
  const t2 = match.participant2_team_id
  const key = `veto-${match.id}`
  const start = async () => {
    if (!t1 || !t2) {
      window.alert('Beide deelnemers moeten een team zijn (geen solo/user-inschrijving) voor map-veto.')
      return
    }
    setBusy(key)
    try {
      const r = await mapvetoAPI.create({
        team_a_id: t1, team_b_id: t2, game: 'cs16',
        tournament_id: tournamentId, match_id: match.id,
      })
      onRefresh()
      if (r.data?.uuid) navigate(`/mapveto/${r.data.uuid}`)
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusy(null) }
  }

  if (veto) {
    const isKlaar = veto.current_turn === 'done'
    return (
      <Link to={`/mapveto/${veto.uuid}`} className="no-underline">
        <div className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-live/30 bg-live/5 hover:border-live">
          <Crosshair className="h-3.5 w-3.5 text-live shrink-0" />
          <span className="font-semibold text-live">Map-veto</span>
          <span className="text-muted-foreground truncate">
            {isKlaar
              ? <>→ <span className="text-live font-mono uppercase">{veto.winner_map}</span></>
              : <>beurt aan team <span className="uppercase font-mono">{veto.current_turn}</span></>}
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground ml-auto shrink-0" />
        </div>
      </Link>
    )
  }

  return (
    <Button size="sm" variant="outline" onClick={start} disabled={busy === key || !t1 || !t2}
            title={(!t1 || !t2) ? 'Beide deelnemers moeten een team zijn' : 'Start map-veto voor deze match'}>
      <Crosshair className="h-3 w-3 mr-1" />
      {busy === key ? 'Bezig…' : 'Start map-veto'}
    </Button>
  )
}

/* Match ↔ server-koppeling voor overlay-context (F1-overlay).
   Een kleine dropdown per actieve match: "deze match speelt op server X".
   Zodra gezet, weet elk overlay-kanaal dat aan deze server hangt hoe de
   teams heten — dus "Bears vs KVK" ipv "team_a vs team_b" op OBS. */
function ServerKoppelBlok({ tournamentId, match, serverList, busy, setBusy, onRefresh }) {
  if (!serverList || !serverList.length) return null
  const active = serverList.find(s => s.active_match_id === match.id)
  const key = `srv-${match.id}`
  const zet = async (serverId) => {
    setBusy(key)
    try {
      await tournamentsAPI.setMatchServer(tournamentId, match.id,
        serverId ? Number(serverId) : null)
      onRefresh()
    } catch (e) {
      window.alert(e.response?.data?.error || e.message)
    } finally { setBusy(null) }
  }
  return (
    <div className="flex items-center gap-2 text-xs">
      <Server className="h-3 w-3 text-muted-foreground shrink-0" />
      <span className="text-muted-foreground shrink-0">Op server:</span>
      <select value={active?.id || ''} onChange={e => zet(e.target.value)}
              disabled={busy === key}
              className="flex-1 h-7 text-xs bg-background border border-border rounded px-1">
        <option value="">— niet gekoppeld —</option>
        {serverList.map(s => (
          <option key={s.id} value={s.id}>
            {s.display_name || s.name} ({s.game_type})
            {s.active_match_id && s.active_match_id !== match.id ? ' · bezet' : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

/* FFA-heat kaart voor de regie: toont deelnemers van de actieve heat +
   link naar detail waar admin per-deelnemer scores invoert. */
function FFAHeatKaart({ tournamentId, match, onRefresh }) {
  const scores = (match.deathmatch_scores || []).slice()
    .sort((a, b) => (a.placement || 999) - (b.placement || 999) || (b.score || 0) - (a.score || 0))
  return (
    <div className="rounded-md border border-live/30 bg-live/5 p-3 space-y-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="live-dot">Bezig</span>
        <span className="text-muted-foreground">
          Ronde {match.round} · Heat {match.group_number || match.match_number} · {scores.length} spelers
        </span>
      </div>
      <div className="space-y-1">
        {scores.map((s, i) => (
          <div key={s.participant_id}
               className="flex items-center gap-2 text-sm px-2 py-1 rounded bg-card/60">
            <span className="w-5 text-xs font-mono text-muted-foreground">{s.placement || i + 1}</span>
            <span className="flex-1 truncate">{s.team_name || s.username || `#${s.participant_id}`}</span>
            <span className="font-mono tabular-nums font-bold">{s.score || 0}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end">
        <Link to={`/tournaments/${tournamentId}`}
              className="text-xs font-mono uppercase tracking-widest text-acid">
          Scores invoeren →
        </Link>
      </div>
    </div>
  )
}

/* Filter-strip: ≤ 5 toernooien = knoppen op één regel; meer = dropdown.
   "Alles" is een aparte knop links; actieve keuze is acid gehighlight. */
function FilterStrip({ alle, filterId, setFilterId }) {
  const uniqById = Array.from(new Map(alle.map(d => [d.tournament.id, d.tournament])).values())
  const gebruikDropdown = uniqById.length > 5
  if (gebruikDropdown) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">Toernooi:</span>
        <select
          className="border rounded px-2 py-1 bg-background flex-1 max-w-md"
          value={filterId ?? ''}
          onChange={e => setFilterId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Alle ({uniqById.length})</option>
          {uniqById.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <Button
        size="sm"
        variant={filterId == null ? 'default' : 'outline'}
        onClick={() => setFilterId(null)}
      >
        Alle ({uniqById.length})
      </Button>
      {uniqById.map(t => (
        <Button
          key={t.id}
          size="sm"
          variant={filterId === t.id ? 'default' : 'outline'}
          onClick={() => setFilterId(t.id)}
        >
          {t.name}
        </Button>
      ))}
    </div>
  )
}

/* Hold-to-fire "Winnaar" — 1 sec ingedrukt houden voor de match wordt
   afgesloten. Voorkomt dat een per-ongeluk-tap op deze rij plotseling de
   winnaar bepaalt (=onomkeerbaar in bracket-formats). Progress-balk vult
   tijdens het vasthouden; loslaten vóór voltooiing annuleert. */
function HoldWinnerButton({ onFire, disabled, accent = '#B4FF39', duration = 1000 }) {
  const [progress, setProgress] = useState(0)
  const startedRef = useRef(0)
  const rafRef = useRef(null)
  const cancel = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    startedRef.current = 0
    setProgress(0)
  }
  const tick = () => {
    const p = Math.min(1, (Date.now() - startedRef.current) / duration)
    setProgress(p)
    if (p >= 1) { cancel(); onFire?.() }
    else rafRef.current = requestAnimationFrame(tick)
  }
  const start = (e) => {
    if (disabled || startedRef.current) return
    e.preventDefault?.()
    startedRef.current = Date.now()
    rafRef.current = requestAnimationFrame(tick)
  }
  useEffect(() => () => cancel(), [])
  return (
    <button type="button" disabled={disabled}
            onPointerDown={start}
            onPointerUp={cancel}
            onPointerLeave={cancel}
            onPointerCancel={cancel}
            className="relative w-full h-7 rounded-md border text-xs font-semibold overflow-hidden select-none disabled:opacity-30 disabled:pointer-events-none"
            style={{ borderColor: accent, color: progress > 0 ? '#000' : accent, background: progress > 0 ? undefined : `${accent}18` }}>
      <div className="absolute inset-y-0 left-0 transition-none"
           style={{ width: `${progress * 100}%`, background: accent }} />
      <div className="relative flex items-center justify-center gap-1">
        <CheckCircle2 className="h-3 w-3" />
        <span>{progress > 0 ? `Bevestigen ${Math.round(progress * 100)}%` : 'Winnaar (hold)'}</span>
      </div>
    </button>
  )
}
